// src/pages/tenant/TenantBillingPage.tsx
import React from 'react';
import {
  Box, Typography, Paper, Grid, Stack, Button, Skeleton, Alert,
  Divider, IconButton, Tooltip, useMediaQuery, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, CircularProgress,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';

import PaymentsIcon from '@mui/icons-material/Payments';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import SettingsIcon from '@mui/icons-material/Settings';
import BusinessIcon from '@mui/icons-material/Business';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

// ─── Types ────────────────────────────────────────────────────────────────────
type BillingSettings = {
  _id?: string;
  currency: string;
  baseFee: number;
  perDeviceRate: number;
  billingDayOfMonth: number;
  dueDays: number;
  exchangeRate: number;
  exchangeRateUpdatedAt?: string;
  bankDetails: { bankName: string; accountNumber: string; accountName: string; sortCode: string };
  invoiceNotes: string;
  isConfigured: boolean;
};

type BillingOverview = {
  totalCompanies: number;
  isConfigured: boolean;
  invoicesThisMonth: number;
  totalBilledThisMonth: number;
  totalPaidThisMonth: number;
  totalOutstanding: number;
  overdueCount: number;
  currency: string;
};

type Company = {
  _id: string;
  name: string;
  email: string;
  isActive: boolean;
  currentInvoice?: {          
    _id: string;
    status: string;           // 'sent' | 'paid' | 'overdue' | 'void' | 'draft'
    invoiceNumber: string;
    total: number;
    dueAt: string;
  } | null;
};
const CURRENCIES = ['NGN', 'USD', 'GBP', 'EUR', 'GHS', 'KES', 'ZAR'];

const fmt = (n: number, currency = 'NGN') => {
  try { return new Intl.NumberFormat('en-NG', { style: 'currency', currency }).format(n); }
  catch { return `${currency} ${Number(n).toFixed(2)}`; }
};

// ─── Settings Dialog ──────────────────────────────────────────────────────────
interface SettingsDialogProps {
  open: boolean;
  existing: BillingSettings | null;
  onClose: () => void;
  onSaved: () => void;
}

const EMPTY_SETTINGS: Omit<BillingSettings, '_id' | 'isConfigured'> = {
  currency: 'NGN', baseFee: 0, perDeviceRate: 0,
  billingDayOfMonth: 1, dueDays: 7, exchangeRate: 1,
  bankDetails: { bankName: '', accountNumber: '', accountName: '', sortCode: '' },
  invoiceNotes: '',
};

const SettingsDialog: React.FC<SettingsDialogProps> = ({ open, existing, onClose, onSaved }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [form, setForm] = React.useState({ ...EMPTY_SETTINGS });
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (existing) {
      setForm({
        currency: existing.currency || 'NGN',
        baseFee: existing.baseFee ?? 0,
        perDeviceRate: existing.perDeviceRate ?? 0,
        billingDayOfMonth: existing.billingDayOfMonth ?? 1,
        dueDays: existing.dueDays ?? 7,
        exchangeRate: existing.exchangeRate ?? 1,
        bankDetails: existing.bankDetails || { bankName: '', accountNumber: '', accountName: '', sortCode: '' },
        invoiceNotes: existing.invoiceNotes || '',
      });
    }
  }, [existing, open]);

  const border = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const subtext = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)';
  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2, fontSize: '0.875rem',
      background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
      '& fieldset': { borderColor: border },
      '&:hover fieldset': { borderColor: '#34d399' },
      '&.Mui-focused fieldset': { borderColor: '#34d399' },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: '#34d399' },
  };

  const set = (path: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (path.startsWith('bankDetails.')) {
      const key = path.split('.')[1];
      setForm(p => ({ ...p, bankDetails: { ...p.bankDetails, [key]: e.target.value } }));
    } else {
      setForm(p => ({ ...p, [path]: e.target.value }));
    }
  };

  const handleSave = async () => {
    setError('');
    setLoading(true);
    try {
      await api.post('/tenant-admin/billing/settings', form);
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const previewTotal = (devices: number) => Number(form.baseFee) + Number(form.perDeviceRate) * devices;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3, background: isDark ? '#1e1e1e' : '#fff', border: `1px solid ${border}` } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ width: 32, height: 32, borderRadius: 1.5, background: 'rgba(52,211,153,0.12)', display: 'grid', placeItems: 'center' }}>
            <SettingsIcon sx={{ color: '#34d399', fontSize: 18 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 900, fontSize: '0.95rem', color: isDark ? '#fff' : '#0f172a' }}>
              Billing Settings
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: subtext }}>Universal — applies to all companies</Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
        <Grid container spacing={2}>
          {/* Pricing */}
          <Grid item xs={12}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: subtext, textTransform: 'uppercase', letterSpacing: '0.07em', mb: 0.5 }}>Pricing</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth label="Currency" value={form.currency} onChange={set('currency')} size="small" sx={fieldSx}>
              {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Base Fee / month" type="number" value={form.baseFee} onChange={set('baseFee')} size="small" sx={fieldSx} inputProps={{ min: 0 }} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Per Device / month" type="number" value={form.perDeviceRate} onChange={set('perDeviceRate')} size="small" sx={fieldSx} inputProps={{ min: 0 }} />
          </Grid>

          {/* Preview */}
          <Grid item xs={12}>
            <Box sx={{ p: 1.5, borderRadius: 2, background: isDark ? 'rgba(52,211,153,0.08)' : 'rgba(52,211,153,0.05)', border: '1px solid rgba(52,211,153,0.2)' }}>
              <Typography sx={{ fontSize: '0.75rem', color: subtext, mb: 0.5 }}>Monthly bill preview:</Typography>
              <Stack direction="row" spacing={3}>
                {[5, 10, 20].map(n => (
                  <Box key={n}>
                    <Typography sx={{ fontSize: '0.65rem', color: subtext }}>{n} devices</Typography>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: '#34d399' }}>
                      {fmt(previewTotal(n), form.currency)}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Grid>

          {/* Schedule */}
          <Grid item xs={12}><Divider sx={{ borderColor: border }} /></Grid>
          <Grid item xs={12}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: subtext, textTransform: 'uppercase', letterSpacing: '0.07em', mb: 0.5 }}>Schedule</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Bill on day of month" type="number" value={form.billingDayOfMonth} onChange={set('billingDayOfMonth')} size="small" sx={fieldSx} inputProps={{ min: 1, max: 28 }} helperText="1–28" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Payment due (days after)" type="number" value={form.dueDays} onChange={set('dueDays')} size="small" sx={fieldSx} inputProps={{ min: 1 }} />
          </Grid>

          {/* Exchange rate */}
          <Grid item xs={12}><Divider sx={{ borderColor: border }} /></Grid>
          <Grid item xs={12}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: subtext, textTransform: 'uppercase', letterSpacing: '0.07em', mb: 0.5 }}>Exchange Rate</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Exchange rate" type="number" value={form.exchangeRate} onChange={set('exchangeRate')} size="small" sx={fieldSx} inputProps={{ min: 0, step: 0.0001 }} helperText="Set 1 if billing in NGN" />
          </Grid>

          {/* Bank details */}
          <Grid item xs={12}><Divider sx={{ borderColor: border }} /></Grid>
          <Grid item xs={12}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: subtext, textTransform: 'uppercase', letterSpacing: '0.07em', mb: 0.5 }}>Bank Details (shown on every invoice)</Typography>
          </Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Bank Name" value={form.bankDetails.bankName} onChange={set('bankDetails.bankName')} size="small" sx={fieldSx} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Account Name" value={form.bankDetails.accountName} onChange={set('bankDetails.accountName')} size="small" sx={fieldSx} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Account Number" value={form.bankDetails.accountNumber} onChange={set('bankDetails.accountNumber')} size="small" sx={fieldSx} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Sort Code (optional)" value={form.bankDetails.sortCode} onChange={set('bankDetails.sortCode')} size="small" sx={fieldSx} /></Grid>

          {/* Notes */}
          <Grid item xs={12}><Divider sx={{ borderColor: border }} /></Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Invoice notes (optional)" value={form.invoiceNotes} onChange={set('invoiceNotes')} size="small" sx={fieldSx} multiline rows={2} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
        <Button onClick={onClose} disabled={loading} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, color: subtext }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={loading}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800, background: '#34d399', color: '#000', '&:hover': { background: '#2ecc71' } }}>
          {loading ? <CircularProgress size={18} /> : 'Save Settings'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const TenantBillingPage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const [settings, setSettings] = React.useState<BillingSettings | null>(null);
  const [overview, setOverview] = React.useState<BillingOverview | null>(null);
  const [companies, setCompanies] = React.useState<Company[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [generating, setGenerating] = React.useState<string | null>(null);
  
  const [genSuccess, setGenSuccess] = React.useState<string | null>(null);
 
  // ── Cooldown helpers backed by localStorage (survives refresh) ──────────
  const COOLDOWN_KEY = 'billing_cooldowns';
  const COOLDOWN_MS  = 5 * 60 * 1000; // 5 minutes
 
  const getCooldowns = (): Record<string, number> => {
    try { return JSON.parse(localStorage.getItem(COOLDOWN_KEY) || '{}'); }
    catch { return {}; }
  };
 
  const setCooldown = (companyId: string) => {
    const cd = getCooldowns();
    cd[companyId] = Date.now() + COOLDOWN_MS;
    // Prune expired entries
    Object.keys(cd).forEach(k => { if (cd[k] < Date.now()) delete cd[k]; });
    localStorage.setItem(COOLDOWN_KEY, JSON.stringify(cd));
  };
 
  const isCoolingDown = (companyId: string): number => {
    const cd = getCooldowns();
    const expiry = cd[companyId] || 0;
    return expiry > Date.now() ? Math.ceil((expiry - Date.now()) / 1000) : 0;
  };
 
  // Force re-render every second while any cooldown is active
  const [, setTick] = React.useState(0);
  React.useEffect(() => {
    const timer = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(timer);
  }, []);
 

  const surface = isDark ? 'rgba(255,255,255,0.03)' : '#ffffff';
  const border = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const subtext = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)';
  const headingColor = isDark ? '#ffffff' : '#0f172a';

  const fetchAll = async () => {
    setLoading(true);
    setError('');
    try {
      const [settingsRes, overviewRes, companiesRes] = await Promise.all([
        api.get('/tenant-admin/billing/settings'),
        api.get('/tenant-admin/billing/overview'),
        api.get('/tenant-admin/billing/companies'),
      ]);
      setSettings(settingsRes.data.data);
      setOverview(overviewRes.data.data);
      setCompanies(Array.isArray(companiesRes.data.data) ? companiesRes.data.data : []);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { fetchAll(); }, []);

  const handleGenerateInvoice = async (companyId: string, companyName: string) => {
    setGenerating(companyId);
    setGenSuccess(null);
    setError('');
    try {
      const res = await api.post('/tenant-admin/invoices/generate', { companyId });
      setGenSuccess(`Invoice ${res.data.data.invoiceNumber} generated for ${companyName}`);
      // Set 5-minute cooldown for this company
      setCooldown(companyId);
      fetchAll();
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Failed to generate invoice';
      setError(msg);
      // If cooldown error from backend, set local cooldown too
      if (msg.includes('cooldown') || msg.includes('already exists') || msg.includes('already paid')) {
        setCooldown(companyId);
      }
    } finally {
      setGenerating(null);
    }
  };

  const currency = overview?.currency || settings?.currency || 'NGN';

  const kpis = [
    { label: 'Companies', value: overview?.totalCompanies ?? 0, color: '#38bdf8', bg: 'rgba(56,189,248,0.10)', icon: BusinessIcon },
    { label: 'Billed This Month', value: fmt(overview?.totalBilledThisMonth ?? 0, currency), color: '#a78bfa', bg: 'rgba(167,139,250,0.10)', icon: TrendingUpIcon },
    { label: 'Collected', value: fmt(overview?.totalPaidThisMonth ?? 0, currency), color: '#34d399', bg: 'rgba(52,211,153,0.10)', icon: PaymentsIcon },
    { label: 'Outstanding', value: fmt(overview?.totalOutstanding ?? 0, currency), color: '#fbbf24', bg: 'rgba(251,191,36,0.10)', icon: ReceiptLongIcon },
    { label: 'Overdue', value: overview?.overdueCount ?? 0, color: '#f87171', bg: 'rgba(248,113,113,0.10)', icon: WarningAmberIcon },
  ];

  return (
    <Box>
      <SettingsDialog
        open={settingsOpen}
        existing={settings}
        onClose={() => setSettingsOpen(false)}
        onSaved={fetchAll}
      />

      {/* ── Header ── */}
      <Stack direction={isMobile ? 'column' : 'row'} alignItems={isMobile ? 'flex-start' : 'center'} justifyContent="space-between" spacing={1.5} sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ width: 36, height: 36, borderRadius: 2, background: 'rgba(52,211,153,0.12)', display: 'grid', placeItems: 'center' }}>
            <PaymentsIcon sx={{ color: '#34d399', fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.2, color: headingColor }}>Billing</Typography>
            <Typography variant="body2" sx={{ color: subtext, fontWeight: 500 }}>Universal billing for all companies</Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchAll} size="small" sx={{ border: `1px solid ${border}`, borderRadius: 1.5, color: subtext }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<SettingsIcon />} onClick={() => setSettingsOpen(true)}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800, fontSize: '0.78rem', background: '#34d399', color: '#000', '&:hover': { background: '#2ecc71' } }}>
            {settings?.isConfigured ? 'Edit Settings' : 'Configure Billing'}
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
      {genSuccess && <Alert severity="success" onClose={() => setGenSuccess(null)} sx={{ mb: 2, borderRadius: 2 }}
        action={<Button size="small" onClick={() => navigate('/tenant/invoices')} endIcon={<OpenInNewIcon fontSize="small" />} sx={{ textTransform: 'none', fontWeight: 800, fontSize: '0.75rem' }}>View Invoices</Button>}>
        {genSuccess}
      </Alert>}

      {/* ── Not configured warning ── */}
      {!loading && !settings?.isConfigured && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2, background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.2)', color: '#fbbf24', '& .MuiAlert-icon': { color: '#fbbf24' } }}>
          Billing is not configured yet. Click <strong>Configure Billing</strong> to set your rates, bank details and billing schedule.
        </Alert>
      )}

      {/* ── KPIs ── */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {kpis.map(k => (
          <Grid item xs={6} sm={4} md key={k.label}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: `1px solid ${border}`, background: surface }}>
              {loading ? <Skeleton height={52} /> : (
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: subtext, textTransform: 'uppercase', letterSpacing: '0.07em' }}>{k.label}</Typography>
                    <Typography sx={{ fontWeight: 900, fontSize: '1.1rem', color: headingColor, mt: 0.25 }}>{k.value}</Typography>
                  </Box>
                  <Box sx={{ width: 36, height: 36, borderRadius: 1.5, background: k.bg, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                    <k.icon sx={{ fontSize: 18, color: k.color }} />
                  </Box>
                </Stack>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* ── Current settings summary ── */}
      {settings?.isConfigured && (
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${border}`, background: surface, mb: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 900, fontSize: '0.85rem', color: headingColor }}>Current Billing Rates</Typography>
            <Button size="small" startIcon={<EditIcon fontSize="small" />} onClick={() => setSettingsOpen(true)}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, fontSize: '0.72rem', color: subtext, '&:hover': { color: '#34d399' } }}>
              Edit
            </Button>
          </Stack>
          <Grid container spacing={2}>
            {[
              { label: 'Currency', value: settings.currency },
              { label: 'Base Fee', value: fmt(settings.baseFee, settings.currency) + ' / month' },
              { label: 'Per Device', value: fmt(settings.perDeviceRate, settings.currency) + ' / device / month' },
              { label: 'Billing Day', value: `${settings.billingDayOfMonth}${['st', 'nd', 'rd'][settings.billingDayOfMonth - 1] || 'th'} of month` },
              { label: 'Due', value: `${settings.dueDays} days after invoice` },
              { label: 'Exchange Rate', value: String(settings.exchangeRate) },
            ].map(item => (
              <Grid item xs={6} sm={4} key={item.label}>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: subtext, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{item.label}</Typography>
                <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: headingColor, mt: 0.25 }}>{item.value}</Typography>
              </Grid>
            ))}
          </Grid>

          {settings.bankDetails?.accountNumber && (
            <>
              <Divider sx={{ borderColor: border, my: 2 }} />
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <AccountBalanceIcon sx={{ fontSize: 16, color: '#38bdf8' }} />
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: subtext, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Bank Details</Typography>
              </Stack>
              <Typography sx={{ fontSize: '0.82rem', color: headingColor }}>
                {settings.bankDetails.bankName} — {settings.bankDetails.accountName} — {settings.bankDetails.accountNumber}
                {settings.bankDetails.sortCode ? ` (${settings.bankDetails.sortCode})` : ''}
              </Typography>
            </>
          )}
        </Paper>
      )}

      {/* ── Companies — generate invoice ── */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${border}`, background: surface, overflow: 'hidden' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 1.75 }}>
          <Box>
            <Typography sx={{ fontWeight: 900, fontSize: '0.85rem', color: headingColor }}>Generate Invoices</Typography>
            <Typography sx={{ fontSize: '0.72rem', color: subtext, mt: 0.25 }}>Invoices auto-generate on the {settings?.billingDayOfMonth || 1}st. Use ▶ to generate manually.</Typography>
          </Box>
          <Button size="small" startIcon={<OpenInNewIcon fontSize="small" />} onClick={() => navigate('/tenant/invoices')}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, fontSize: '0.72rem', color: subtext, '&:hover': { color: '#fbbf24' } }}>
            View All Invoices
          </Button>
        </Stack>
        <Divider sx={{ borderColor: border }} />

        {loading ? (
          <Box sx={{ p: 3 }}><Stack spacing={1}>{[1, 2, 3].map(i => <Skeleton key={i} height={52} sx={{ borderRadius: 1 }} />)}</Stack></Box>
        ) : companies.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <BusinessIcon sx={{ fontSize: 32, color: subtext, mb: 1 }} />
            <Typography sx={{ fontWeight: 800, color: headingColor }}>No active companies</Typography>
          </Box>
        ) : (
          <Stack spacing={0}>
            {companies.map((c, i) => (
              <Box key={c._id} sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: i < companies.length - 1 ? `1px solid ${border}` : 'none' }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ width: 30, height: 30, borderRadius: 1.5, background: 'rgba(167,139,250,0.12)', display: 'grid', placeItems: 'center' }}>
                    <BusinessIcon sx={{ color: '#a78bfa', fontSize: 16 }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: headingColor }}>{c.name}</Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: subtext }}>{c.email}</Typography>
                  </Box>
                </Stack>
                {(() => {
                const secondsLeft    = isCoolingDown(c._id);
                const onCooldown     = secondsLeft > 0;
                const alreadyPaid    = c.currentInvoice?.status === 'paid';
                const hasPendingInv  = c.currentInvoice && c.currentInvoice.status !== 'void' && c.currentInvoice.status !== 'paid';
                const isDisabled     = !settings?.isConfigured || generating === c._id || onCooldown || alreadyPaid;
 
                const tooltipMsg = !settings?.isConfigured
                  ? 'Configure billing settings first'
                  : alreadyPaid
                  ? `Already paid this month (${c.currentInvoice?.invoiceNumber})`
                  : onCooldown
                  ? `Please wait ${secondsLeft}s before generating another invoice`
                  : hasPendingInv
                  ? `Invoice ${c.currentInvoice?.invoiceNumber} already sent — regenerate?`
                  : 'Generate invoice now';
 
                const btnColor = alreadyPaid ? '#34d399' : onCooldown ? '#94a3b8' : '#34d399';
                const btnBg    = alreadyPaid
                  ? 'rgba(52,211,153,0.08)'
                  : onCooldown
                  ? 'rgba(148,163,184,0.12)'
                  : 'rgba(52,211,153,0.12)';
                const btnLabel = generating === c._id
                  ? 'Generating…'
                  : alreadyPaid
                  ? `Paid ✓`
                  : onCooldown
                  ? `Wait ${secondsLeft}s`
                  : 'Generate Invoice';
 
                return (
                  <Tooltip title={tooltipMsg}>
                    <span>
                      <Button
                        size="small"
                        startIcon={generating === c._id
                          ? <CircularProgress size={12} />
                          : alreadyPaid
                          ? <CheckCircleIcon fontSize="small" />
                          : <PlayArrowIcon fontSize="small" />
                        }
                        disabled={isDisabled}
                        onClick={() => handleGenerateInvoice(c._id, c.name)}
                        sx={{
                          borderRadius: 2, textTransform: 'none', fontWeight: 800, fontSize: '0.72rem',
                          background: btnBg, color: btnColor,
                          '&:hover': { background: 'rgba(52,211,153,0.2)' },
                          '&:disabled': { opacity: alreadyPaid ? 0.7 : 0.4 },
                        }}
                      >
                        {btnLabel}
                      </Button>
                    </span>
                  </Tooltip>
                );
              })()}
              </Box>
            ))}
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

export default TenantBillingPage;